import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS } from "../../data/site";

export default function Nav() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 16);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    return (
        <>
        <header
            data-testid="site-nav"
            className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
                scrolled
                    ? "border-b border-slate-200/80 bg-white/85 shadow-sm backdrop-blur-xl"
                    : "border-b border-transparent bg-white/60 backdrop-blur-md"
            }`}
        >
            <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-16">
                <Logo />
                <nav className="hidden items-center gap-9 md:flex" aria-label="Primary">
                    {NAV_LINKS.map((link) => (
                        <NavLink
                            key={link.to}
                            to={link.to}
                            data-testid={`nav-link-${link.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `group relative text-sm font-medium transition-colors duration-300 ${
                                    isActive ? "text-slate-900" : "text-slate-500 hover:text-slate-900"
                                }`
                            }
                        >
                            {({ isActive }) => (
                                <>
                                    {link.label}
                                    <span
                                        className={`absolute -bottom-1.5 left-0 h-[2px] rounded-full bg-brand transition-[width] duration-300 ${
                                            isActive ? "w-full" : "w-0 group-hover:w-full"
                                        }`}
                                    />
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>
                <div className="flex items-center gap-3">
                    <Link
                        to="/contact"
                        data-testid="nav-start-project-btn"
                        className="group hidden items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-white transition-[background-color,transform] duration-300 hover:bg-brand-dark active:scale-95 sm:flex"
                    >
                        Start a Project
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                    <button
                        type="button"
                        data-testid="nav-mobile-menu-btn"
                        onClick={() => setOpen(true)}
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 text-slate-700 md:hidden"
                        aria-label="Open menu"
                    >
                        <Menu className="h-5 w-5" />
                    </button>
                </div>
            </div>
        </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-50 flex flex-col bg-white md:hidden"
                    >
                        <div className="flex h-[76px] items-center justify-between border-b border-slate-200 px-4">
                            <Logo />
                            <button
                                type="button"
                                data-testid="mobile-menu-close-btn"
                                onClick={() => setOpen(false)}
                                className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 text-slate-700"
                                aria-label="Close menu"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <nav className="flex flex-1 flex-col justify-center gap-2 px-8" aria-label="Mobile">
                            {[{ label: "Home", to: "/" }, ...NAV_LINKS].map((link, i) => (
                                <motion.div
                                    key={link.to}
                                    initial={{ opacity: 0, y: 24 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 + i * 0.07, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    <NavLink
                                        to={link.to}
                                        data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                                        className={({ isActive }) =>
                                            `block border-b border-slate-100 py-5 font-display text-4xl font-medium tracking-tight transition-colors duration-300 ${
                                                isActive ? "text-brand" : "text-slate-900"
                                            }`
                                        }
                                    >
                                        {link.label}
                                    </NavLink>
                                </motion.div>
                            ))}
                            <motion.div
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5, duration: 0.5 }}
                            >
                                <Link
                                    to="/contact"
                                    data-testid="mobile-start-project-btn"
                                    className="mt-10 flex items-center justify-center gap-2 rounded-full bg-brand px-6 py-4 font-semibold text-white transition-transform duration-200 active:scale-95"
                                >
                                    Start a Project
                                    <ArrowUpRight className="h-5 w-5" />
                                </Link>
                            </motion.div>
                        </nav>
                        <p className="px-8 pb-10 font-mono text-xs uppercase tracking-[0.25em] text-slate-400">
                            Based in India · Working Worldwide
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
